import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Cookie, Settings, Eye, BarChart3, Mail } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #474545;
  padding: 80px 20px 60px;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 40px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-5px);
    color: #FAECEC;
  }
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: #FAECEC;
  opacity: 0.7;
  font-size: 0.95rem;
`;

const Content = styled(motion.div)`
  background: rgba(0, 151, 178, 0.05);
  border: 1px solid rgba(0, 151, 178, 0.2);
  border-radius: 20px;
  padding: 50px;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Section = styled.section`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #0097B2;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const SubsectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #FAECEC;
  margin: 24px 0 16px;
`;

const Paragraph = styled.p`
  color: #FAECEC;
  opacity: 0.9;
  line-height: 1.8;
  margin-bottom: 16px;
  font-size: 1rem;
`;

const List = styled.ul`
  color: #FAECEC;
  opacity: 0.9;
  line-height: 1.8;
  margin-left: 24px;
  margin-bottom: 16px;
`;

const ListItem = styled.li`
  margin-bottom: 12px;
`;

const Highlight = styled.span`
  color: #0097B2;
  font-weight: 600;
`;

const CookieTypeCard = styled.div`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.08) 0%, rgba(250, 236, 236, 0.03) 100%);
  border: 1px solid rgba(0, 151, 178, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin: 16px 0;
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.1) 0%, rgba(250, 236, 236, 0.05) 100%);
  border: 1px solid rgba(0, 151, 178, 0.3);
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactTitle = styled.h4`
  color: #FAECEC;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 4px;
`;

const ContactEmail = styled.a`
  color: #0097B2;
  text-decoration: none;
  font-size: 1rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CookiePolicy = () => {
  return (
    <PageContainer>
      <Container>
        <BackLink to="/">
          <ArrowLeft size={20} />
          Back to Home
        </BackLink>

        <Header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Title>Cookie Policy</Title>
          <LastUpdated>Last updated: January 2026</LastUpdated>
        </Header>

        <Content
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Section>
            <Paragraph>
              This Cookie Policy explains how <Highlight>f01i.ai</Highlight> and <Highlight>f01i.app</Highlight> use cookies 
              and similar technologies to recognize you when you visit our platform. It explains what these technologies are, 
              why we use them, and your rights to control our use of them.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>
              <Cookie size={24} />
              What Are Cookies?
            </SectionTitle>
            <Paragraph>
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. 
              They are widely used to make websites work more efficiently and provide information to website owners.
            </Paragraph>
            <Paragraph>
              Cookies allow us to recognize your device and remember information about your visit, such as your preferences and 
              settings. This helps us provide you with a better, more personalized experience.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>
              <Eye size={24} />
              Types of Cookies We Use
            </SectionTitle>
            
            <CookieTypeCard>
              <SubsectionTitle>1. Essential Cookies</SubsectionTitle>
              <Paragraph>
                These cookies are necessary for our platform to function properly. They enable core functionality such as:
              </Paragraph>
              <List>
                <ListItem>User authentication and account security</ListItem>
                <ListItem>Session management and keeping you logged in</ListItem>
                <ListItem>Load balancing and system stability</ListItem>
                <ListItem>Security features and fraud prevention</ListItem>
              </List>
              <Paragraph>
                <Highlight>You cannot opt out of essential cookies</Highlight> as they are strictly necessary for the platform to work.
              </Paragraph>
            </CookieTypeCard>

            <CookieTypeCard>
              <SubsectionTitle>2. Functional Cookies</SubsectionTitle>
              <Paragraph>
                These cookies enable enhanced functionality and personalization:
              </Paragraph>
              <List>
                <ListItem>Remembering your preferences and settings (language, theme, timezone)</ListItem>
                <ListItem>Storing your trading bot configurations</ListItem>
                <ListItem>Remembering which f01i Blocks you've created</ListItem>
                <ListItem>Personalizing your dashboard and interface</ListItem>
              </List>
              <Paragraph>
                Without these cookies, some features may not function properly.
              </Paragraph>
            </CookieTypeCard>

            <CookieTypeCard>
              <SubsectionTitle>3. Analytics Cookies</SubsectionTitle>
              <Paragraph>
                These cookies help us understand how visitors interact with our platform:
              </Paragraph>
              <List>
                <ListItem>Which pages and features are most popular</ListItem>
                <ListItem>How long users spend on different sections</ListItem>
                <ListItem>Identifying technical issues and errors</ListItem>
                <ListItem>Improving user experience based on usage patterns</ListItem>
              </List>
              <Paragraph>
                All analytics data is aggregated and anonymous. We cannot identify you personally from this data.
              </Paragraph>
            </CookieTypeCard>

            <CookieTypeCard>
              <SubsectionTitle>4. Performance Cookies</SubsectionTitle>
              <Paragraph>
                These cookies help us optimize platform performance:
              </Paragraph>
              <List>
                <ListItem>Monitoring server response times</ListItem>
                <ListItem>Tracking API call success rates</ListItem>
                <ListItem>Optimizing bot execution and trading speeds</ListItem>
                <ListItem>Detecting and preventing system overload</ListItem>
              </List>
            </CookieTypeCard>
          </Section>

          <Section>
            <SectionTitle>
              <BarChart3 size={24} />
              Third-Party Cookies
            </SectionTitle>
            <Paragraph>
              We may use trusted third-party services that set their own cookies:
            </Paragraph>
            <List>
              <ListItem>
                <Highlight>Cloud Infrastructure:</Highlight> Our hosting providers may use cookies for security and performance
              </ListItem>
              <ListItem>
                <Highlight>Payment Processors:</Highlight> NowPayments and other payment services may use cookies to process transactions
              </ListItem>
              <ListItem>
                <Highlight>Analytics Tools:</Highlight> We may use analytics services to understand user behavior and improve our platform
              </ListItem>
            </List>
            <Paragraph>
              These third parties have their own privacy policies. We recommend reviewing them to understand how they use cookies.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>
              <Settings size={24} />
              How to Control Cookies
            </SectionTitle>
            <Paragraph>
              You have several options to control or limit how cookies are used:
            </Paragraph>
            
            <SubsectionTitle>Browser Settings</SubsectionTitle>
            <Paragraph>
              Most web browsers allow you to control cookies through their settings:
            </Paragraph>
            <List>
              <ListItem>Block all cookies</ListItem>
              <ListItem>Delete existing cookies</ListItem>
              <ListItem>Allow cookies only from specific websites</ListItem>
              <ListItem>Get notified when websites try to set cookies</ListItem>
            </List>
            <Paragraph>
              Check your browser's help section for instructions on managing cookies. Note that blocking all cookies may prevent 
              you from using certain features of our platform.
            </Paragraph>

            <SubsectionTitle>Cookie Consent</SubsectionTitle>
            <Paragraph>
              When you first visit our website, we'll ask for your consent to use non-essential cookies. You can:
            </Paragraph>
            <List>
              <ListItem>Accept all cookies</ListItem>
              <ListItem>Decline non-essential cookies</ListItem>
              <ListItem>Customize your preferences</ListItem>
            </List>
            <Paragraph>
              You can change your cookie preferences at any time by clearing your browser cookies and revisiting our website.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>How Long Do Cookies Last?</SectionTitle>
            <Paragraph>
              The duration cookies remain on your device depends on their type:
            </Paragraph>
            <List>
              <ListItem>
                <Highlight>Session Cookies:</Highlight> Temporary cookies that are deleted when you close your browser
              </ListItem>
              <ListItem>
                <Highlight>Persistent Cookies:</Highlight> Remain on your device for a set period (typically 30 days to 1 year) 
                or until you manually delete them
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Cookies and Personal Data</SectionTitle>
            <Paragraph>
              Some cookies may collect personal information, such as your IP address or account preferences. 
              We handle all data collected through cookies in accordance with our{' '}
              <Link to="/privacy-policy" style={{ color: '#0097B2', textDecoration: 'underline' }}>Privacy Policy</Link>.
            </Paragraph>
            <Paragraph>
              We never use cookies to:
            </Paragraph>
            <List>
              <ListItem>Store sensitive financial information or passwords</ListItem>
              <ListItem>Access your exchange API keys or trading credentials</ListItem>
              <ListItem>Track your location without permission</ListItem>
              <ListItem>Sell your data to third parties</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Updates to This Policy</SectionTitle>
            <Paragraph>
              We may update this Cookie Policy from time to time to reflect changes in technology or legal requirements. 
              We will notify you of any material changes by posting the updated policy on this page and updating the 
              "Last updated" date.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Contact Us</SectionTitle>
            <Paragraph>
              If you have questions about our use of cookies or this Cookie Policy, please contact us:
            </Paragraph>
            <ContactCard>
              <ContactIcon>
                <Mail size={24} color="#FAECEC" />
              </ContactIcon>
              <ContactInfo>
                <ContactTitle>Email Us</ContactTitle>
                <ContactEmail href="mailto:feedback@f01i.ai">feedback@f01i.ai</ContactEmail>
              </ContactInfo>
            </ContactCard>
          </Section>

          <Section>
            <Paragraph>
              By continuing to use our platform, you acknowledge that you have read and understood this Cookie Policy.
            </Paragraph>
          </Section>
        </Content>
      </Container>
    </PageContainer>
  );
};

export default CookiePolicy;
